import { useEffect } from "react";

const SEARCH_INPUT_SELECTOR = [
  "input[data-cmd-f-search]",
  "textarea[data-cmd-f-search]",
  "input[type='search']",
  "input[role='searchbox']",
  "textarea[role='searchbox']",
  "input[placeholder*='search' i]",
  "textarea[placeholder*='search' i]",
  "input[aria-label*='search' i]",
  "textarea[aria-label*='search' i]",
].join(",");

type SearchInput = HTMLInputElement | HTMLTextAreaElement;

const isVisibleSearchInput = (input: SearchInput): boolean => {
  if (input.disabled || input.readOnly || input.closest("[hidden], [inert], [aria-hidden='true']")) {
    return false;
  }

  const style = window.getComputedStyle(input);
  return style.display !== "none" && style.visibility !== "hidden" && input.getClientRects().length > 0;
};

export const findVisibleSearchInput = (): SearchInput | undefined => {
  const inputs = Array.from(document.querySelectorAll<SearchInput>(SEARCH_INPUT_SELECTOR)).filter(
    isVisibleSearchInput
  );
  const activeElement = document.activeElement;

  return (
    inputs.find((input) => input === activeElement) ??
    inputs.find((input) => input.dataset.cmdFSearch === "primary") ??
    inputs[0]
  );
};

export const handleFocusSearchShortcut = (event: KeyboardEvent): boolean => {
  if (
    event.defaultPrevented ||
    !(event.metaKey || event.ctrlKey) ||
    event.altKey ||
    event.shiftKey ||
    event.key.toLowerCase() !== "f"
  ) {
    return false;
  }

  const input = findVisibleSearchInput();
  if (!input) return false;

  event.preventDefault();
  event.stopImmediatePropagation();
  input.focus();
  input.select();
  return true;
};

/** Gives visible in-app search fields priority over the browser's Cmd/Ctrl+F UI. */
export function useFocusSearchShortcut() {
  useEffect(() => {
    window.addEventListener("keydown", handleFocusSearchShortcut, true);
    return () => window.removeEventListener("keydown", handleFocusSearchShortcut, true);
  }, []);
}
